(() => {
  const confirmationLog = window.BHConfirmationLog || ((level, eventName, details = {}) => {
    const entry = { time: new Date().toISOString(), event: eventName, ...details };
    window.__BH_CONFIRMATION_LOGS__ = window.__BH_CONFIRMATION_LOGS__ || [];
    window.__BH_CONFIRMATION_LOGS__.push(entry);
    if (window.__BH_CONFIRMATION_LOGS__.length > 150) window.__BH_CONFIRMATION_LOGS__.shift();
    const method = typeof console[level] === 'function' ? level : 'log';
    console[method]('[BH Confirmation]', eventName, entry);
    return entry;
  });
  window.BHConfirmationLog = confirmationLog;
  window.BHConfirmationDebug = window.BHConfirmationDebug || (() => {
    const elements = [...document.querySelectorAll('bh-cart-confirmation')];
    const diagnostic = {
      time: new Date().toISOString(),
      customElementDefined: Boolean(customElements.get('bh-cart-confirmation')),
      loaderAvailable: typeof window.BHEnsureCartConfirmation === 'function',
      componentCount: elements.length,
      components: elements.map((element, index) => ({
        index,
        connected: element.isConnected,
        upgraded: typeof element.open === 'function',
        parent: element.parentElement?.tagName || null,
        dialogFound: Boolean(element.querySelector('[data-bh-confirmation-dialog]')),
        dialogOpen: Boolean(element.querySelector('[data-bh-confirmation-dialog]')?.open)
      })),
      logs: [...(window.__BH_CONFIRMATION_LOGS__ || [])],
      variantLogs: [...(window.__BH_VARIANT_DEBUG_LOGS__ || [])],
      modalLogs: [...(window.__BH_CARD_MODAL_LOGS__ || [])]
    };
    console.log('[BH Confirmation Debug]', diagnostic);
    console.table(diagnostic.logs);
    return diagnostic;
  });
  if (customElements.get('bh-product-actions')) return;
  const storageKey = 'bh-product-wishlist:v1';
  const confirmationScriptLoads = new Map();

  const loadConfirmationScript = source => {
    if (!source || customElements.get('bh-cart-confirmation')) {
      confirmationLog('info', 'loader:skip', {
        hasSource: Boolean(source),
        alreadyDefined: Boolean(customElements.get('bh-cart-confirmation'))
      });
      return Promise.resolve();
    }
    if (!confirmationScriptLoads.has(source)) {
      confirmationLog('info', 'loader:start', { source });
      confirmationScriptLoads.set(source, new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = source;
        script.async = true;
        script.addEventListener('load', () => {
          confirmationLog('info', 'loader:loaded', {
            source,
            customElementDefined: Boolean(customElements.get('bh-cart-confirmation'))
          });
          resolve();
        }, { once: true });
        script.addEventListener('error', () => {
          confirmationLog('error', 'loader:error', { source });
          reject(new Error('Confirmation script failed to load'));
        }, { once: true });
        document.head.append(script);
      }).catch(error => {
        confirmationScriptLoads.delete(source);
        throw error;
      }));
    }
    return confirmationScriptLoads.get(source);
  };

  class BHProductActions extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.sizeButton = this.querySelector('[data-bh-actions-size]');
      this.wishButton = this.querySelector('[data-bh-actions-wish]');
      this.shareButton = this.querySelector('[data-bh-actions-share]');
      this.picker = this.querySelector('[data-bh-actions-picker]');
      this.status = this.querySelector('[data-bh-actions-status]');
      this.primaryLabel = this.querySelector('[data-bh-actions-primary-label]');
      this.description = this.querySelector('[data-bh-actions-description]');
      this.descriptionText = this.querySelector('[data-bh-actions-description-text]');
      this.descriptionToggle = this.querySelector('[data-bh-actions-description-toggle]');
      this.directAdd = this.sizeButton.hasAttribute('data-bh-actions-direct-add');
      confirmationLog('info', 'actions:connected', {
        productId: this.dataset.productId || null,
        directAdd: this.directAdd,
        confirmationElementFound: Boolean(document.querySelector('bh-cart-confirmation')),
        confirmationScriptUrl: this.dataset.confirmationScriptUrl || null
      });
      try {
        this.variantAvailability = JSON.parse(this.dataset.variantAvailability || '{}');
      } catch (_) {
        this.variantAvailability = {};
      }
      this.picker.hidden = true;
      this.sizeButton.disabled = false;
      this.wishButton.disabled = false;
      this.shareButton.disabled = false;
      this.sizeButton.addEventListener('click', (event) => {
        this.isolateAction(event);
        if (this.directAdd) this.addToCart();
        else this.openSize();
      }, { signal });
      this.wishButton.addEventListener('click', (event) => {
        this.isolateAction(event);
        this.toggleWish();
      }, { signal });
      this.shareButton.addEventListener('click', (event) => {
        this.isolateAction(event);
        this.share();
      }, { signal });
      [this.wishButton, this.shareButton].forEach((button) => {
        ['pointerdown', 'pointerup', 'touchstart', 'touchend'].forEach((eventName) => {
          button.addEventListener(eventName, (event) => event.stopPropagation(), { signal, passive: true });
        });
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.picker.hidden) {
          event.preventDefault();
          this.picker.hidden = true;
          this.sizeButton.setAttribute('aria-expanded', 'false');
          this.sizeButton.focus({ preventScroll: true });
        }
      }, { signal });
      window.addEventListener('storage', (event) => {
        if (event.key === storageKey || event.key === null) this.syncWish();
      }, { signal });
      window.addEventListener('bh:wishlist-updated', () => this.syncWish(), { signal });
      ['bh:variant:change', 'variant:change'].forEach((eventName) => {
        document.addEventListener(eventName, (event) => this.handleVariantChange(event), { signal });
      });
      this.setupDescription(signal);
      window.BHEnsureCartConfirmation = detail => this.openConfirmation(detail);
      if (document.querySelector('bh-cart-confirmation')) {
        loadConfirmationScript(this.dataset.confirmationScriptUrl).catch(() => {});
      }
      if (this.directAdd) this.syncDirectAddButton();
      this.syncWish();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.descriptionObserver?.disconnect();
      window.clearTimeout(this.feedbackTimer);
      this.controller = null;
    }

    isolateAction(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    setupDescription(signal) {
      if (!this.description || !this.descriptionText || !this.descriptionToggle) return;
      const refresh = () => {
        if (this.description.classList.contains('is-expanded')) return;
        const isOverflowing = this.description.hasAttribute('data-description-long')
          || this.descriptionText.scrollHeight > this.descriptionText.clientHeight + 1;
        this.descriptionToggle.hidden = !isOverflowing;
      };
      this.descriptionToggle.addEventListener('click', (event) => {
        this.isolateAction(event);
        const expanded = this.description.classList.toggle('is-expanded');
        this.descriptionToggle.setAttribute('aria-expanded', String(expanded));
        this.descriptionToggle.textContent = expanded ? this.dataset.descriptionLess : this.dataset.descriptionMore;
      }, { signal });
      requestAnimationFrame(refresh);
      if ('ResizeObserver' in window) {
        this.descriptionObserver = new ResizeObserver(refresh);
        this.descriptionObserver.observe(this.description);
      }
    }

    openSize() {
      const api = window.BHModal;
      const state = api?.getState?.();
      const modal = document.getElementById(`BHVariantModal-${this.dataset.sectionId}`);
      if (modal && typeof api?.open === 'function' && String(state?.sectionId) === this.dataset.sectionId) {
        try {
          api.open();
          if (api.getState().modalOpen) {
            this.picker.hidden = true;
            this.sizeButton.setAttribute('aria-expanded', 'false');
            return;
          }
        } catch (_) {}
      }
      this.picker.hidden = !this.picker.hidden;
      this.sizeButton.setAttribute('aria-expanded', String(!this.picker.hidden));
      if (!this.picker.hidden) {
        this.picker.querySelector('button:not(:disabled), input:not([type="hidden"]):not(:disabled), select:not(:disabled)')?.focus({ preventScroll: true });
      }
    }

    resolveVariantId() {
      const gallery = document.getElementById(`VariantMediaGallery-${this.dataset.sectionId}`);
      const galleryId = gallery?.dataset?.currentVariant;
      if (galleryId && /^\d+$/.test(galleryId)) return galleryId;
      if (this.currentVariantId && /^\d+$/.test(this.currentVariantId)) return this.currentVariantId;
      const form = document.getElementById(`product-form-${this.dataset.sectionId}`);
      const formId = form?.querySelector('[name="id"]')?.value;
      if (formId && /^\d+$/.test(formId)) return formId;
      const urlId = new URL(window.location.href).searchParams.get('variant');
      if (urlId && /^\d+$/.test(urlId)) return urlId;
      return /^\d+$/.test(this.sizeButton.dataset.variantId || '') ? this.sizeButton.dataset.variantId : '';
    }

    handleVariantChange(event) {
      const detail = event.detail || {};
      if (detail.productId && String(detail.productId) !== this.dataset.productId) return;
      if (detail.sectionId && String(detail.sectionId) !== this.dataset.sectionId) return;
      const variant = detail.variant || {};
      if (variant.product_id && String(variant.product_id) !== this.dataset.productId) return;
      const variantId = String(variant.id || detail.variantId || '');
      if (!/^\d+$/.test(variantId)) return;
      this.currentVariantId = variantId;
      if (typeof variant.available === 'boolean') this.variantAvailability[variantId] = variant.available;
      if (this.directAdd) this.syncDirectAddButton(variantId);
    }

    syncDirectAddButton(variantId = this.resolveVariantId()) {
      if (!this.directAdd) return;
      const available = this.variantAvailability[String(variantId)] !== false;
      const addLabel = this.dataset.cartLabel
        || this.primaryLabel?.dataset.fallbackLabel
        || 'Agregar al carrito';
      this.sizeButton.dataset.variantId = variantId;
      this.sizeButton.disabled = !variantId || !available || this.adding;
      if (!this.adding && this.primaryLabel) {
        this.primaryLabel.textContent = available ? addLabel : this.dataset.soldOut;
      }
    }

    async refreshCart() {
      const root = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!response.ok) return;
        const cart = await response.json();
        ['cart:updated', 'cart:refresh', 'theme:cart:update'].forEach((eventName) => {
          document.dispatchEvent(new CustomEvent(eventName, {
            bubbles: true,
            detail: { cart, source: 'bh-product-actions' }
          }));
        });
        document.querySelectorAll('.cart-count, [data-cart-count], .cart-count-bubble span[aria-hidden="true"]').forEach((element) => {
          element.textContent = cart.item_count;
          element.classList.remove('hidden');
          element.closest('.cart-count-bubble')?.classList.remove('hidden');
        });
        window.BHCartUI?.refresh?.();
      } catch (_) {}
    }

    async addToCart() {
      if (this.adding) return;
      const variantId = this.resolveVariantId();
      confirmationLog('info', 'actions:add-start', {
        productId: this.dataset.productId || null,
        variantId: variantId || null
      });
      if (!variantId) {
        this.announce(this.dataset.addError);
        return;
      }
      this.syncDirectAddButton(variantId);
      if (this.sizeButton.disabled) return;
      const root = window.Shopify?.routes?.root || '/';
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      this.adding = true;
      this.sizeButton.disabled = true;
      this.sizeButton.classList.add('is-loading');
      if (this.primaryLabel) this.primaryLabel.textContent = this.dataset.adding;
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || this.dataset.addError);
        const addedItem = Array.isArray(data.items) ? data.items[0] : data;
        confirmationLog('info', 'actions:add-success', {
          productId: String(addedItem?.product_id || this.dataset.productId || ''),
          variantId: String(addedItem?.variant_id || addedItem?.id || variantId)
        });
        document.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { item: addedItem, source: 'bh-product-actions' }
        }));
        const confirmationDetail = {
          item: addedItem,
          productId: String(addedItem?.product_id || this.dataset.productId),
          variantId: String(addedItem?.variant_id || addedItem?.id || variantId),
          productTitle: String(addedItem?.product_title || this.dataset.productTitle || ''),
          source: 'bh-product-actions'
        };
        await this.openConfirmation(confirmationDetail);
        await this.refreshCart();
        this.announce(this.dataset.added);
      } catch (error) {
        confirmationLog('error', 'actions:add-error', { message: error?.message || String(error) });
        this.announce(error?.message || this.dataset.addError);
      } finally {
        this.adding = false;
        this.sizeButton.classList.remove('is-loading');
        this.syncDirectAddButton(variantId);
      }
    }

    async openConfirmation(detail) {
      let confirmation = document.querySelector('bh-cart-confirmation');
      confirmationLog('info', 'actions:open-request', {
        productId: detail?.productId || null,
        variantId: detail?.variantId || null,
        elementFound: Boolean(confirmation),
        elementUpgraded: typeof confirmation?.open === 'function'
      });
      if (typeof confirmation?.open !== 'function') {
        await loadConfirmationScript(this.dataset.confirmationScriptUrl).catch(() => {});
        confirmation = document.querySelector('bh-cart-confirmation');
        confirmationLog('info', 'actions:open-after-load', {
          elementFound: Boolean(confirmation),
          elementUpgraded: typeof confirmation?.open === 'function'
        });
      }
      if (typeof confirmation?.open === 'function') {
        confirmationLog('info', 'actions:open-direct', { variantId: detail?.variantId || null });
        confirmation.open(detail);
        return;
      }
      confirmationLog('warn', 'actions:open-event-fallback', { variantId: detail?.variantId || null });
      document.dispatchEvent(new CustomEvent('bh:cart-confirmation', {
        bubbles: true,
        detail
      }));
    }

    readWishlist() {
      const items = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(items)) throw new Error('Invalid wishlist');
      return items.filter((item) => item && typeof item === 'object' && typeof item.id === 'string');
    }

    syncWish() {
      try {
        const selected = this.readWishlist().some((item) => item.id === this.dataset.productId);
        this.wishButton.setAttribute('aria-pressed', String(selected));
        this.wishButton.setAttribute('aria-label', selected ? this.dataset.wishRemove : this.dataset.wishAdd);
      } catch (_) {
        this.wishButton.setAttribute('aria-pressed', 'false');
        this.wishButton.setAttribute('aria-label', this.dataset.wishAdd);
      }
    }

    toggleWish() {
      try {
        const items = this.readWishlist();
        const selected = items.some((item) => item.id === this.dataset.productId);
        const next = items.filter((item) => item.id !== this.dataset.productId);
        if (!selected) next.push({ id: this.dataset.productId, title: this.dataset.productTitle, url: this.dataset.productUrl });
        localStorage.setItem(storageKey, JSON.stringify(next));
        window.dispatchEvent(new CustomEvent('bh:wishlist-updated'));
        this.announce(selected ? this.dataset.wishRemoved : this.dataset.wishSaved);
      } catch (_) {
        this.announce(this.dataset.wishError);
      }
    }

    announce(message) {
      window.clearTimeout(this.feedbackTimer);
      this.status.textContent = message;
      this.toggleAttribute('data-feedback-visible', true);
      this.feedbackTimer = window.setTimeout(() => {
        this.removeAttribute('data-feedback-visible');
        this.status.textContent = '';
      }, 2200);
    }

    async copyText(text) {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (_) {}
      }
      const field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.left = '-9999px';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (_) {}
      field.remove();
      this.shareButton.focus({ preventScroll: true });
      return copied;
    }

    async share() {
      if (this.sharing) return;
      this.sharing = true;
      const url = new URL(this.dataset.productUrl, window.location.origin);
      const variantId = this.resolveVariantId();
      if (variantId && /^\d+$/.test(variantId)) url.searchParams.set('variant', variantId);
      try {
        const copied = await this.copyText(url.href);
        this.announce(copied ? this.dataset.linkCopied : this.dataset.copyError);
      } finally {
        this.sharing = false;
      }
    }
  }

  customElements.define('bh-product-actions', BHProductActions);
})();
